import { Component, OnInit, OnDestroy, ViewChild, ElementRef, inject } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { NgClass, AsyncPipe, NgOptimizedImage } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ImageModifierPipe } from '../image-modifer.pipe';

interface ShuffleConfig {
  enforcePositionConstraints?: boolean;
  positionThreshold?: number;
}

@Component({
    standalone: true,
    selector: 'tea-shop-fe-home-carousel',
    templateUrl: './home-carousel.component.html',
    styleUrl: './home-carousel.component.css',
    imports: [NgClass, RouterLink, AsyncPipe, ImageModifierPipe, NgOptimizedImage]
})
export class HomeCarouselComponent implements OnInit, OnDestroy {
  private logger = inject(NGXLogger);


  items = [
    {
      name: 'Bi Luo Chun',
      image: '/assets/home-carousel/bi-lo-chun-carousel-0136.jpg',
      slug: 'bi-lo-chun'
    },
    {
      name: 'Da Yu Ling',
      image: '/assets/home-carousel/da-yu-ling-carousel-0142.jpg',
      slug: 'da-yu-ling'
    },
    {
      name: 'Dong Ding',
      image: '/assets/home-carousel/dong-ding-carousel-1158.jpg',
      slug: 'dong-ding'
    },
    {
      name: 'Genmaicha',
      image: '/assets/home-carousel/genmaicha-carousel-0101.jpg',
      slug: 'genmaicha'
    },
    {
      name: 'Gyokuro',
      image: '/assets/home-carousel/gyokuro-carousel-1668.jpg',
      slug: 'gyokuro'
    },
    {
      name: 'Himalaya Shangri-La',
      image: '/assets/home-carousel/himalaya-shangri-la-carousel-1078.jpg',
      slug: 'himalaya-shangri-la'
    },
    {
      name: 'Houjicha',
      image: '/assets/home-carousel/houjicha-carousel-1633.jpg',
      slug: 'houjicha'
    },
    {
      name: 'Jasmine Pearl',
      image: '/assets/home-carousel/jasmine-pearl-carousel-1167.jpg',
      slug: 'jasmine-pearl'
    },
    {
      name: 'Jin Jun Mei',
      image: '/assets/home-carousel/jin-jun-mei-carousel-1192.jpg',
      slug: 'jin-jun-mei'
    },
    {
      name: 'Oriental Beauty',
      image: '/assets/home-carousel/oriental-beauty-carousel-1289.jpg',
      slug: 'oriental-beauty'
    },
    {
      name: 'Purple Da Hong Pao',
      image: '/assets/home-carousel/pdhp-carousel-1684.jpg',
      slug: 'purple-da-hong-pao'
    },
    {
      name: 'Sencha',
      image: '/assets/home-carousel/sencha-carousel-1602.jpg',
      slug: 'sencha'
    },
    {
      name: 'Silver Needle',
      image: '/assets/home-carousel/silver-needle-carousel-1520.jpg',
      slug: 'silver-needle'
    },
    {
      name: 'White Everest',
      image: '/assets/home-carousel/white-everest-carousel-1269.jpg',
      slug: 'white-everest'
    },
    {
      name: 'Kirribilli Breakfast',
      image: '/assets/home-carousel/kirribilli-breakfast-carousel.jpg',
      slug: 'kirribilli-breakfast'
    },
  ];

  @ViewChild('container') container!: ElementRef;

  currentIndex = 0;
  private intervalId: any;
  private previousOrder: string[] = [];
  private readonly DEFAULT_CONFIG: ShuffleConfig = {
    enforcePositionConstraints: true,
    positionThreshold: 3
  };
  private enableAutoSlide = true;

  ngOnInit() {
    this.previousOrder = this.items.map(item => item.slug);
    this.items = this.improvedShuffle(this.items, this.DEFAULT_CONFIG);
    this.items = this.improvedShuffle(this.items, { enforcePositionConstraints: false });
    this.enableAutoSlide = false;
    this.startAutoSlide();
  }

  ngOnDestroy() {
    this.stopAutoSlide();
  }

  startAutoSlide() {
    if (this.enableAutoSlide) {
      this.intervalId = setInterval(() => {
        this.next();
      }, 5000); // Change slide every 5 seconds
    }
  }

  stopAutoSlide() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  next() {
    const nextIndex = (this.currentIndex + 1) % this.items.length;
    this.setCurrentIndex(nextIndex);
  }

  previous() {
    const nextIndex = this.currentIndex === 0 ? 
      this.items.length - 1 : 
      this.currentIndex - 1;
    this.setCurrentIndex(nextIndex);
  }

  goToSlide(index: number) {
    this.setCurrentIndex(index);
  }

  setCurrentIndex(index: number): void {
    this.currentIndex = index;

    // Wait for DOM update
    setTimeout(() => {
      try {
        const container = this.container.nativeElement;
        const itemsContainer = container.querySelector('.flex');
        const selectedItem = itemsContainer.children[index];
        
        container.scrollTo({
          left: selectedItem.offsetLeft - (container.clientWidth - selectedItem.offsetWidth) / 2,
          behavior: 'smooth'
        });
      } catch (error) {
        this.logger.error('Error scrolling to item:', error);
      }
    }, 0);
  }

  private improvedShuffle<T extends { slug: string }>(items: T[], config?: ShuffleConfig): T[] {
    const shuffleConfig = { ...this.DEFAULT_CONFIG, ...config };
    const n = items.length;
    const result = [...items];
    let isValidShuffle = false;

    while (!isValidShuffle) {
      // Fisher-Yates shuffle
      for (let i = n - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [result[i], result[j]] = [result[j], result[i]];
      }

      isValidShuffle = !shuffleConfig.enforcePositionConstraints || 
        this.isShuffleValid(result, shuffleConfig.positionThreshold);
    }

    this.previousOrder = result.map(item => item.slug);
    return result;
  }

  private isShuffleValid(newOrder: { slug: string }[], threshold = 3): boolean {
    if (!this.previousOrder.length) return true;

    let maxPositionChange = 0;
    for (let i = 0; i < newOrder.length; i++) {
      const previousIndex = this.previousOrder.indexOf(newOrder[i].slug);
      const positionChange = Math.abs(i - previousIndex);
      maxPositionChange = Math.max(maxPositionChange, positionChange);

      if (positionChange < threshold) {
        return false;
      }
    }

    return maxPositionChange >= threshold;
  }
}
